import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from "@angular/forms";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { MatDialog } from "@angular/material/dialog";
import { NGXLogger } from "ngx-logger";
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { StatusTrackerInterface } from 'src/app/shared/interfaces/status-tracker.interface';
import { BrandType } from 'src/app/signup/models/brand-type';
import { SectionStatusService } from 'src/app/status-tracker/section-status.service';
import { BASK_BRAND_NAME, TCB_BRAND_NAME } from "../../shared/constants";
import { AppValidator } from "../../shared/interfaces/AppValidator";
import { ValidationDetail } from "../../shared/model/ValidationDetail";
import { FacadeService } from "../../shared/service-facade/facade.service";
import { SignupService } from "../../signup/service/signup.service";
import { ReviewService } from "../services/review.service";

const ALL_TERMS_CONDITIONS_CONTROL_NAME = 'allTermsConditionsCheckbox';
const TAXPAYER_CERTIFICATION_CONTROL_NAME = "taxPayerCertificationCheckbox";

@Component({
  selector: 'app-terms-and-conditions',
  templateUrl: './terms-and-conditions.component.html',
  styleUrls: ['./terms-and-conditions.component.scss']
})
export class TermsAndConditionsComponent implements OnInit, OnDestroy, StatusTrackerInterface, AppValidator {

  @ViewChild('eSign') eSign!: ElementRef;

  destroySubject$: Subject<void> = new Subject();

  termsAndConditionsForm = this.formBuilder.group({
    allTermsConditionsCheckbox: [false, [Validators.requiredTrue]],
    taxPayerCertificationCheckbox: [false, [Validators.requiredTrue]]
  });

  dialogOpen = false;

  public allTermsConditionsAccepted: boolean;
  public taxPayerCertificationAccepted: boolean;

  reviewSubmissionPending : boolean = false;

  validationDetail = new ValidationDetail(this.termsAndConditionsForm, new Map<string, string>());

  dialogRef : any;
  brandName = '';

  constructor(private formBuilder: FormBuilder,
              private facadeService: FacadeService,
              private statusTrackerService: SectionStatusService,
              private signupService: SignupService,
              private logger: NGXLogger,
              public dialog: MatDialog,
              private reviewService : ReviewService,
  ) {
    this.allTermsConditionsAccepted = this.signupService.allTermsConditionsAccepted;
    this.taxPayerCertificationAccepted = this.signupService.taxPayerCertificationAccepted;
    this.termsAndConditionsForm.get(ALL_TERMS_CONDITIONS_CONTROL_NAME)?.setValue(this.allTermsConditionsAccepted);
    this.termsAndConditionsForm.get(TAXPAYER_CERTIFICATION_CONTROL_NAME)?.setValue(this.taxPayerCertificationAccepted);

    this.signupService.reviewSubmissionStatusChange.subscribe(value => {
      this.reviewSubmissionPending = value;
    });
  }

  /**
   * Init
   */
  ngOnInit(): void {
    this.setupStatusTracker();
    this.setupValidationMap();
    this.setupValidationTrigger();

    this.reviewService.termsConditionsAcceptanceProcessComplete.pipe(takeUntil(this.destroySubject$)).subscribe(async next => {
      this.dialog.closeAll();
      this.dialogOpen = false;

      this.allTermsConditionsAccepted = true;
    });

    this.dialog.afterAllClosed.subscribe(value => {
      this.dialogOpen = false;
    });

    this.brandName = (Number(this.facadeService.getBrandId()) === BrandType.BaskBank)
      ? BASK_BRAND_NAME
      : TCB_BRAND_NAME;
  }

  setupValidationTrigger(): void {
    this.statusTrackerService.onTriggerValidations.subscribe(() => {
      this.termsAndConditionsForm.markAllAsTouched();
      this.termsAndConditionsForm.updateValueAndValidity();
    });
  };

  ngOnDestroy(): void {
      this.destroySubject$.next();
  }
  /***
   *  Get status changes for form and update status tracker.
   */
  setupStatusTracker(): void {
    this.termsAndConditionsForm.statusChanges.pipe(takeUntil(this.destroySubject$)).subscribe(validChange => {
      this.logger.debug("Terms and conditions, Status Change:" + validChange);
      this.updateValidations();
    });
  }

  /**
   * Called to see if the form is dirty and has been modified.
   */
  formIsDirty(): boolean {
    if (this.termsAndConditionsForm.controls[ALL_TERMS_CONDITIONS_CONTROL_NAME]?.dirty) return true;

    return false;
  }

  /**
   * Call to update validations in the validations component.
   */
  updateValidations(): void {
    this.signupService.validationsChange.emit(this.validationDetail);
  }

  /**
   * Setup validation map for labels.
   */
  setupValidationMap(): void {
    let labelMap = new Map<string, string>();
    labelMap.set(ALL_TERMS_CONDITIONS_CONTROL_NAME, "Terms and Conditions");
    this.validationDetail = new ValidationDetail(this.termsAndConditionsForm, labelMap);
  }

  /**
   * On acceptance of disclosures, write a separate event to the application logs.
   * @param event
   */
  async onAcceptDisclosure(event: MatCheckboxChange) {
    if(event.source.id === ALL_TERMS_CONDITIONS_CONTROL_NAME) {
      if(event.checked && this.allTermsConditionsAccepted) {
        this.signupService.termsAccepted.next(true);
      }
    }
    this.signupService.allTermsConditionsAccepted = event.checked;
  }

  /**
   * On IRS Verification complete, write a separate event to the application logs.
   * @param event
   */
  async onIrsVerification(event: MatCheckboxChange) {
    this.signupService.taxPayerCertificationAccepted = event.checked;
  }

}
